/**
 * Type-safe session storage utilities
 * Centralized session storage operations with proper typing
 */

import { AUTH_CONSTANTS } from '../constants/auth';
import type { VerificationSessionData } from '../types';

/**
 * Gets a value from sessionStorage with type safety
 * @param key - The session storage key
 * @returns The stored value or null if not found
 */
export function getSessionItem(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch (error) {
    console.error('Error reading from sessionStorage:', error);
    return null;
  }
}

/**
 * Sets a value in sessionStorage
 * @param key - The session storage key
 * @param value - The value to store
 */
export function setSessionItem(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch (error) {
    console.error('Error writing to sessionStorage:', error);
  }
}

/**
 * Removes a value from sessionStorage
 * @param key - The session storage key
 */
export function removeSessionItem(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    console.error('Error removing from sessionStorage:', error);
  }
}

/**
 * Gets and parses JSON from sessionStorage with type safety
 * @param key - The session storage key
 * @returns The parsed object or null if not found/invalid
 */
export function getSessionObject<T>(key: string): T | null {
  const item = getSessionItem(key);
  if (!item) return null;
  
  try {
    return JSON.parse(item) as T;
  } catch (error) {
    console.error('Error parsing sessionStorage JSON:', error);
    return null;
  }
}

/**
 * Stores an object in sessionStorage as JSON
 * @param key - The session storage key
 * @param value - The object to store
 */
export function setSessionObject<T>(key: string, value: T): void {
  try {
    const json = JSON.stringify(value);
    setSessionItem(key, json);
  } catch (error) {
    console.error('Error stringifying object for sessionStorage:', error);
  }
}

/**
 * Gets verification session data with proper typing
 * @returns VerificationSessionData or null if not found
 */
export function getVerificationData(): VerificationSessionData | null {
  return getSessionObject<VerificationSessionData>(
    AUTH_CONSTANTS.SESSION_KEYS.VERIFICATION_DATA
  );
}

/**
 * Stores verification session data
 * @param data - The verification session data to store
 */
export function setVerificationData(data: VerificationSessionData): void {
  setSessionObject(AUTH_CONSTANTS.SESSION_KEYS.VERIFICATION_DATA, data);
}

/**
 * Removes verification session data
 */
export function clearVerificationData(): void {
  removeSessionItem(AUTH_CONSTANTS.SESSION_KEYS.VERIFICATION_DATA);
}

/**
 * Gets OAuth CSRF token
 * @returns The CSRF token or null if not found
 */
export function getOAuthCsrfToken(): string | null {
  return getSessionItem(AUTH_CONSTANTS.SESSION_KEYS.OAUTH_CSRF);
}

/**
 * Stores OAuth CSRF token
 * @param token - The CSRF token to store
 */
export function setOAuthCsrfToken(token: string): void {
  setSessionItem(AUTH_CONSTANTS.SESSION_KEYS.OAUTH_CSRF, token);
}

/**
 * Removes OAuth CSRF token
 */
export function clearOAuthCsrfToken(): void {
  removeSessionItem(AUTH_CONSTANTS.SESSION_KEYS.OAUTH_CSRF);
}

/**
 * Clears all authentication-related session data
 */
export function clearAuthSession(): void {
  clearVerificationData();
  clearOAuthCsrfToken();
  removeSessionItem(AUTH_CONSTANTS.SESSION_KEYS.OAUTH_STATE);
  removeSessionItem(AUTH_CONSTANTS.SESSION_KEYS.REDIRECT_AFTER_LOGIN);
}
